import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Palette, Plus, Trash2, Pencil, Check, X, GripVertical, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Category Manager — full CRUD for gear categories.
 *
 * Categories are persisted server-side (`GET/POST/PATCH/DELETE /api/categories`)
 * and hydrated into `AppContext.categories` on load. The `color` field is a
 * hex string used for the dot indicator on inventory rows, `sort_order`
 * controls list ordering. Renaming a category atomically updates every
 * item using it on the server side, and the local `items` array is patched
 * client-side so the UI reflects it without a full refetch.
 *
 * Deletion is guarded — the API returns 409 if any item still references
 * the category. The UI surfaces that as an inline error and disables the
 * delete button whenever `itemCount > 0`.
 */

// Curated palette — matches the original hand-picked colors from mock.js.
// Users can also type any hex value in the color field directly.
const SUGGESTED_COLORS = [
  '#ef4444', '#f59e0b', '#eab308', '#facc15', '#a3e635',
  '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#94a3b8', '#64748b', '#737373',
];

// Small helper — parse "#rrggbb" (or "#rgb") into a [r,g,b] triple for use
// with jsPDF's `setFillColor`. Falls back to mid-grey on any parse error.
function hexToRgb(hex) {
  if (!hex || typeof hex !== 'string') return [115, 115, 115];
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return [115, 115, 115];
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function ColorSwatch({ color, size = 18 }) {
  return (
    <span
      className="inline-block rounded-full border border-neutral-800 shrink-0"
      style={{ background: color, width: size, height: size }}
    />
  );
}

function AddRow({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#737373');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      await onCreate({ name: name.trim(), color });
      toast.success(`Added "${name.trim()}"`);
      setName('');
      setColor('#737373');
      setOpen(false);
    } catch (err) {
      toast.error(err.message || 'Could not add category');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-2"
        data-testid="add-category-btn"
      >
        <Plus size={13} /> Add Category
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="surface rounded-lg p-4 flex flex-wrap items-end gap-3"
      data-testid="add-category-form"
    >
      <div className="flex-1 min-w-[180px]">
        <label className="block text-[10px] tracking-wider uppercase text-neutral-400 mb-1.5">
          Name
        </label>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Drones"
          className="w-full bg-neutral-950 border border-neutral-800 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-500/60"
          data-testid="add-category-name"
        />
      </div>
      <div>
        <label className="block text-[10px] tracking-wider uppercase text-neutral-400 mb-1.5">
          Color
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-9 w-12 rounded border border-neutral-800 bg-neutral-950 cursor-pointer"
            data-testid="add-category-color"
          />
          <input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="w-24 bg-neutral-950 border border-neutral-800 text-white rounded-md px-2 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-amber-500/40"
          />
          <div className="flex items-center gap-0.5 pl-2 border-l border-neutral-800">
            {SUGGESTED_COLORS.slice(0, 10).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                title={c}
                className="h-5 w-5 rounded-full border border-neutral-800 hover:scale-125 transition-transform"
                style={{ background: c }}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={saving}
          className="text-xs px-3 py-2 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="btn-amber text-xs px-4 py-2 rounded-md disabled:opacity-60"
          data-testid="add-category-save"
        >
          {saving ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}

function CategoryRow({ category, itemCount, onSave, onDelete, onViewItems }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);
  const [color, setColor] = useState(category.color);
  const [busy, setBusy] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id, disabled: editing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return toast.error('Name cannot be empty');
    if (trimmed === category.name && color === category.color) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(category.id, { name: trimmed, color });
      toast.success('Category updated');
      setEditing(false);
    } catch (err) {
      toast.error(err.message || 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (itemCount > 0) return;
    if (!window.confirm(`Delete "${category.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await onDelete(category.id);
      toast.success(`"${category.name}" deleted`);
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="grid grid-cols-[24px_1fr_120px_120px_140px] gap-3 items-center px-4 py-3 row-hover"
        data-testid={`category-row-editing-${category.id}`}
      >
        <GripVertical size={13} className="text-neutral-700" />
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && save()}
          className="bg-neutral-950 border border-amber-500/50 text-white rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/40"
        />
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-7 w-10 rounded border border-neutral-800 bg-neutral-950 cursor-pointer"
          />
          <span className="text-[11px] text-neutral-500 font-mono">{color}</span>
        </div>
        <span className="text-[11px] text-neutral-500 text-right">
          {itemCount} item{itemCount === 1 ? '' : 's'}
        </span>
        <div className="flex items-center gap-1 justify-end">
          <button
            onClick={save}
            disabled={busy}
            className="h-7 w-7 grid place-items-center rounded border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
            title="Save"
            data-testid={`category-save-${category.id}`}
          >
            <Check size={12} />
          </button>
          <button
            onClick={() => {
              setName(category.name);
              setColor(category.color);
              setEditing(false);
            }}
            disabled={busy}
            className="h-7 w-7 grid place-items-center rounded border border-neutral-800 text-neutral-400 hover:bg-neutral-900 disabled:opacity-50"
            title="Cancel"
          >
            <X size={12} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid grid-cols-[24px_1fr_120px_120px_140px] gap-3 items-center px-4 py-2.5 row-hover group bg-neutral-950/40"
      data-testid={`category-row-${category.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        aria-label={`Drag to reorder ${category.name}`}
        title="Drag to reorder"
        className="cursor-grab active:cursor-grabbing text-neutral-700 hover:text-amber-400 focus:text-amber-400 focus:outline-none"
        data-testid={`category-drag-${category.id}`}
      >
        <GripVertical size={13} />
      </button>
      <div className="flex items-center gap-3 min-w-0">
        <ColorSwatch color={category.color} />
        <span className="text-sm text-white truncate">{category.name}</span>
      </div>
      <span className="text-[11px] text-neutral-500 font-mono">{category.color}</span>
      <button
        onClick={() => itemCount > 0 && onViewItems(category.name)}
        disabled={itemCount === 0}
        title={itemCount > 0 ? `View the ${itemCount} item${itemCount === 1 ? '' : 's'} using ${category.name}` : 'No items in this category'}
        className={`text-[11px] text-right font-medium ${
          itemCount > 0
            ? 'text-neutral-300 hover:text-amber-400 hover:underline decoration-dotted cursor-pointer'
            : 'text-neutral-600 cursor-default'
        }`}
        data-testid={`category-view-items-${category.id}`}
      >
        {itemCount} item{itemCount === 1 ? '' : 's'}
        {itemCount > 0 && <Eye size={10} className="inline ml-1 -mt-0.5" />}
      </button>
      <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => setEditing(true)}
          className="h-7 w-7 grid place-items-center rounded text-neutral-400 hover:bg-neutral-900 hover:text-amber-400"
          title="Rename / change color"
          data-testid={`category-edit-${category.id}`}
        >
          <Pencil size={11} />
        </button>
        <button
          onClick={remove}
          disabled={itemCount > 0 || busy}
          title={
            itemCount > 0
              ? `Reassign ${itemCount} item${itemCount === 1 ? '' : 's'} to another category before deleting.`
              : 'Delete category'
          }
          className={`h-7 w-7 grid place-items-center rounded ${
            itemCount > 0
              ? 'text-neutral-700 cursor-not-allowed'
              : 'text-neutral-400 hover:bg-rose-500/10 hover:text-rose-400'
          }`}
          data-testid={`category-delete-${category.id}`}
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

export default function Categories() {
  const { categories, items, addCategory, updateCategory, deleteCategory, reorderCategories } = useApp();
  const navigate = useNavigate();

  // Item counts per category name — computed once per items/categories change.
  const countByName = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.category, (m.get(it.category) || 0) + 1));
    return m;
  }, [items]);

  // Total units per category (accounts for multi-qty items like batteries).
  const unitsByName = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.category, (m.get(it.category) || 0) + (it.totalQty || 1)));
    return m;
  }, [items]);

  const sorted = useMemo(
    () => [...categories].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [categories],
  );

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'letter' });
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Gear Categories', 40, 50);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(120);
    doc.text(`Whiplash Film Creative · ${dateStr}`, 40, 66);
    doc.text(
      `${sorted.length} categories · ${items.length} items total`,
      40,
      78,
    );
    doc.setTextColor(0);

    // Body table
    const rows = sorted.map((c) => [
      '',
      c.name,
      c.color.toUpperCase(),
      String(countByName.get(c.name) || 0),
      String(unitsByName.get(c.name) || 0),
    ]);

    autoTable(doc, {
      startY: 96,
      head: [['', 'Category', 'Color', 'Items', 'Units']],
      body: rows,
      styles: { fontSize: 10, cellPadding: 6, lineColor: [230, 230, 230] },
      headStyles: {
        fillColor: [23, 23, 23],
        textColor: [245, 158, 11], // amber accent
        fontStyle: 'bold',
        halign: 'left',
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      columnStyles: {
        0: { cellWidth: 26 },              // color swatch column
        1: { cellWidth: 'auto' },          // name
        2: { cellWidth: 90, font: 'courier', textColor: 100 },
        3: { cellWidth: 60, halign: 'right' },
        4: { cellWidth: 60, halign: 'right' },
      },
      // Draw the color swatch by painting a filled circle in column 0
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          const cat = sorted[data.row.index];
          if (!cat) return;
          const [r, g, b] = hexToRgb(cat.color);
          const cx = data.cell.x + data.cell.width / 2;
          const cy = data.cell.y + data.cell.height / 2;
          doc.setFillColor(r, g, b);
          doc.circle(cx, cy, 5, 'F');
        }
      },
    });

    // Footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(140);
      doc.text(
        `Page ${p} of ${pageCount}`,
        doc.internal.pageSize.width - 40,
        doc.internal.pageSize.height - 20,
        { align: 'right' },
      );
    }

    const fname = `wfc-categories-${now.toISOString().slice(0, 10)}.pdf`;
    doc.save(fname);
    toast.success(`Downloaded ${fname}`);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = sorted.findIndex((c) => c.id === active.id);
    const newIndex = sorted.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const nextOrder = arrayMove(sorted, oldIndex, newIndex).map((c) => c.id);
    try {
      await reorderCategories(nextOrder);
    } catch (e) {
      toast.error(e.message || 'Reorder failed');
    }
  };

  const viewItemsFor = (categoryName) => {
    navigate(`/inventory?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <div className="p-5 max-w-4xl" data-testid="categories-page">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400 mb-1">
            Settings
          </div>
          <h1 className="text-2xl text-white flex items-center gap-2">
            <Palette size={20} className="text-amber-400" /> Categories
          </h1>
          <p className="text-xs text-neutral-500 mt-1 max-w-xl">
            Add, rename, recolor, reorder (drag the ≡ handle), or remove gear
            categories. Renaming updates every item that uses it. Click a
            category&apos;s item count to jump to the filtered inventory.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={downloadPdf}
            disabled={sorted.length === 0}
            title="Download the category list as a PDF"
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900 hover:text-amber-400 hover:border-amber-500/40 flex items-center gap-2 disabled:opacity-50"
            data-testid="download-categories-pdf"
          >
            <Download size={13} /> Download PDF
          </button>
          <AddRow onCreate={addCategory} />
        </div>
      </div>

      <div className="surface rounded-lg overflow-hidden">
        <div className="grid grid-cols-[24px_1fr_120px_120px_140px] gap-3 items-center px-4 py-2.5 border-b border-neutral-900 bg-neutral-950 text-[10px] tracking-wider uppercase text-neutral-500">
          <span />
          <span>Name</span>
          <span>Color</span>
          <span className="text-right">Usage</span>
          <span className="text-right">Actions</span>
        </div>
        {sorted.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            No categories yet.
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sorted.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="divide-y divide-neutral-900">
                {sorted.map((c) => (
                  <CategoryRow
                    key={c.id}
                    category={c}
                    itemCount={countByName.get(c.name) || 0}
                    onSave={updateCategory}
                    onDelete={deleteCategory}
                    onViewItems={viewItemsFor}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
